import { AlertTriangle, Copy, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

/**
 * Detects if the current browser is an embedded webview inside another app
 * (Facebook, Messenger, Instagram, TikTok, Line, etc.). Google's "Use secure
 * browsers" policy (Error 403: disallowed_useragent) blocks OAuth from these
 * webviews — so we need to show a clear path out: open in Safari/Chrome.
 *
 * Detection is best-effort via userAgent sniffing. False negatives are
 * acceptable (users will just see Google's error and we can't do worse).
 */
export function detectInAppBrowser() {
  if (typeof navigator === 'undefined') return null;
  const ua = (navigator.userAgent || '').toLowerCase();

  // Most common Philippine/SEA in-app browsers — covers >95% of cases.
  if (/fbav|fban|fb_iab|fbios/i.test(ua)) return 'Facebook';
  if (/messenger|messengerforios|messengerforiosnative/i.test(ua)) return 'Messenger';
  if (/instagram/i.test(ua)) return 'Instagram';
  if (/tiktok|musical_ly|bytedancewebview|aweme/i.test(ua)) return 'TikTok';
  if (/line\//i.test(ua)) return 'Line';
  if (/twitter|twitterandroid/i.test(ua)) return 'Twitter / X';
  if (/snapchat/i.test(ua)) return 'Snapchat';
  if (/wechat|micromessenger/i.test(ua)) return 'WeChat';
  if (/kakaotalk/i.test(ua)) return 'KakaoTalk';
  // Generic webview hints (some apps use plain WebView without a brand string).
  if (/; wv\)|webview/i.test(ua)) return 'an in-app browser';

  return null;
}

export default function InAppBrowserBanner() {
  const [browser] = useState(() => detectInAppBrowser());
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent || '');

  if (!browser) return null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied! Now open Safari or Chrome and paste it.');
    } catch {
      // Older WebViews block clipboard API — fall back to a manual prompt.
      window.prompt('Copy this link, then open in Safari or Chrome:', url);
    }
  };

  return (
    <div
      data-testid="in-app-browser-banner"
      className="mb-5 bg-[#3b1f0e] border border-[#D4AF37]/40 p-4 text-left"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-sans font-medium mb-1">
            Google Sign-In won&apos;t work inside {browser}
          </p>
          <p className="text-white/70 text-xs font-sans leading-relaxed mb-3">
            Google blocks login from in-app browsers for security
            (<span className="font-mono text-[#D4AF37]/70">Error 403: disallowed_useragent</span>).
            {' '}{isIOS ? 'Tap the share icon (□↑) below and choose "Open in Safari"' : 'Tap the three-dot menu (⋮) and choose "Open in Chrome / browser"'} — or copy the link and paste it into your browser manually.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copyLink}
              data-testid="in-app-copy-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#D4AF37] text-black text-xs font-sans hover:bg-[#F3E5AB] transition-colors"
            >
              <Copy className="w-3 h-3" /> Copy Link
            </button>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="in-app-open-external"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-sans hover:bg-[#D4AF37]/10 transition-colors"
            >
              <ExternalLink className="w-3 h-3" /> Open in browser
            </a>
          </div>
          <p className="text-white/40 text-[11px] font-sans mt-3">
            Or just use email + password below — no in-app browser issue.
          </p>
        </div>
      </div>
    </div>
  );
}
